package com.mps.asc_app

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.runtime.*
import com.mps.asc_app.data.user.UserStatus
import com.mps.asc_app.presentation.MainViewModel
import com.mps.asc_app.presentation.MainNavEvent
import com.mps.asc_app.ui.screens.Blockscreen
import com.mps.asc_app.ui.screens.AdminPanelScreen
import com.mps.asc_app.ui.screens.BookingScreen
import com.mps.asc_app.ui.screens.EmailNotVerifiedScreen
import com.mps.asc_app.ui.screens.ForgotPasswordScreen
import com.mps.asc_app.ui.screens.LoginScreen
import com.mps.asc_app.ui.screens.PadelMembershipFormScreen
import com.mps.asc_app.ui.screens.PrivacyConsentScreen
import com.mps.asc_app.ui.screens.RegisterScreen
import com.mps.asc_app.ui.screens.AscMembershipFormScreen
import com.mps.asc_app.ui.screens.MyGamesScreen
import com.mps.asc_app.ui.screens.ProfileScreen
import com.mps.asc_app.ui.components.AppTab
import com.mps.asc_app.ui.components.AscBottomBar
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth

private enum class AppScreen {
    Login,
    ForgotPassword,
    Register,
    Booking,
    MyGames,
    Profile,
    AdminPanel,
    Blocked,
    AscMembershipForm,
    PadelMembershipForm,
    EmailNotVerified,
    PrivacyConsent,
    Loading,
}

@Composable
fun App(
    mainViewModel: MainViewModel,
    contentPadding: PaddingValues
) {
    val uiState by mainViewModel.uiState.collectAsState()

    var screen by remember { mutableStateOf(AppScreen.Login) }

    // Handle navigation events coming from the top navigation bar (Scaffold topBar)
    LaunchedEffect(Unit) {
        mainViewModel.navEvents.collect { event ->
            val current = mainViewModel.uiState.value
            when (event) {
                MainNavEvent.OpenAdminPanel -> {
                    if (
                        current.isLoggedIn &&
                        current.isAdmin &&
                        !current.isCheckingApproval
                    ) {
                        screen = AppScreen.AdminPanel
                    }
                }

                MainNavEvent.GoHome -> {
                    // Don't override manual navigation to Register/ForgotPassword
                    if (screen == AppScreen.Register || screen == AppScreen.ForgotPassword) return@collect
                    screen = if (current.isLoggedIn) AppScreen.Booking else AppScreen.Login
                }
            }
        }
    }

    // Keep screen in sync with auth + approval state (but don't override manual Register navigation)
    LaunchedEffect(uiState.isLoggedIn, uiState.userStatus, uiState.isCheckingApproval, uiState.forceAscMembershipFormOnly) {

        if (!uiState.isLoggedIn) {
            if (screen != AppScreen.Register && screen != AppScreen.ForgotPassword) {
                screen = AppScreen.Login
            }
            return@LaunchedEffect
        }

        if (uiState.isCheckingApproval) {
            screen = AppScreen.Loading
            return@LaunchedEffect
        }

        if (uiState.needsPrivacyConsent) {
            screen = AppScreen.PrivacyConsent
            return@LaunchedEffect
        }

        if (
            screen == AppScreen.AdminPanel &&
            uiState.isLoggedIn &&
            uiState.isAdmin
        ) {
            return@LaunchedEffect
        }

        // Reiter der Navigation unten nicht durch Status-Aktualisierungen wegspringen lassen.
        val canUseTabs = uiState.isAdmin || uiState.userStatus == UserStatus.APPROVED
        if (canUseTabs && (screen == AppScreen.MyGames || screen == AppScreen.Profile)) {
            return@LaunchedEffect
        }

        if (uiState.isAdmin && uiState.userStatus != UserStatus.EMAIL_NOT_VERIFIED) {
            screen = AppScreen.Booking
            return@LaunchedEffect
        }

        when (uiState.userStatus) {
            UserStatus.APPROVED -> screen = AppScreen.Booking
            UserStatus.PENDING -> {
                screen = AppScreen.Blocked
            }
            UserStatus.EMAIL_NOT_VERIFIED -> screen = AppScreen.EmailNotVerified
            null -> screen = AppScreen.Loading
        }
    }

    when (screen) {
        AppScreen.Booking -> TabShell(AppTab.BOOKING, uiState.isAdmin, onSelectTab = { screen = it.toScreen() }) {
            BookingScreen(
                contentPadding = contentPadding,
                isAdmin = uiState.isAdmin,
                isTrainer = uiState.isTrainer,
                trainerAssignments = uiState.trainerAssignments,
            )
        }

        AppScreen.MyGames -> TabShell(AppTab.MY_GAMES, uiState.isAdmin, onSelectTab = { screen = it.toScreen() }) {
            MyGamesScreen(
                contentPadding = contentPadding,
                onBook = { screen = AppScreen.Booking },
            )
        }

        AppScreen.Profile -> TabShell(AppTab.PROFILE, uiState.isAdmin, onSelectTab = { screen = it.toScreen() }) {
            ProfileScreen(
                email = Firebase.auth.currentUser?.email,
                roleLabel = when {
                    uiState.isAdmin -> "Admin"
                    uiState.isTrainer -> "Trainer"
                    else -> "Mitglied"
                },
                onLogout = { mainViewModel.logout() },
                contentPadding = contentPadding,
            )
        }

        AppScreen.AdminPanel -> TabShell(AppTab.ADMIN, uiState.isAdmin, onSelectTab = { screen = it.toScreen() }) {
            if (uiState.isLoggedIn && uiState.isAdmin) {
                AdminPanelScreen(
                    contentPadding = contentPadding,
                    onBack = { screen = AppScreen.Booking }
                )
            } else {
                BookingScreen(
                    contentPadding = contentPadding,
                    isAdmin = uiState.isAdmin,
                    isTrainer = uiState.isTrainer,
                    trainerAssignments = uiState.trainerAssignments,
                )
            }
        }

        AppScreen.Blocked -> {
            Blockscreen(
                membershipRequestSubmitted = uiState.membershipRequestSubmitted,
                forceAscMembershipFormOnly = uiState.forceAscMembershipFormOnly,
                forcePadelMembershipFormOnly = uiState.forcePadelMembershipFormOnly,
                onRefresh = { mainViewModel.recheckApproval() },
                onLogout = { mainViewModel.logout() },
                onOpenAscMembershipForm = { screen = AppScreen.AscMembershipForm },
                onOpenPadelMembershipForm = { screen = AppScreen.PadelMembershipForm },
            )
        }

        AppScreen.AscMembershipForm -> {
            AscMembershipFormScreen(
                contentPadding = contentPadding,
                onSubmitted = { mainViewModel.recheckApproval() },
                onBack = { screen = AppScreen.Blocked },
            )
        }

        AppScreen.PadelMembershipForm -> {
            PadelMembershipFormScreen(
                contentPadding = contentPadding,
                onSubmitted = { mainViewModel.recheckApproval() },
                onBack = { screen = AppScreen.Blocked },
            )
        }

        AppScreen.EmailNotVerified -> {
            EmailNotVerifiedScreen(
                onRefresh = { mainViewModel.recheckApproval() },
                onLogout = { mainViewModel.logout() },
                onResendEmail = { mainViewModel.resendVerificationEmail() },
                isResending = uiState.isResendingVerificationEmail,
                resendSuccess = uiState.resendEmailSuccess,
                resendError = uiState.resendEmailError,
            )
        }

        AppScreen.PrivacyConsent -> {
            PrivacyConsentScreen(
                onAccept = { mainViewModel.acceptPrivacyConsent() },
                onLogout = { mainViewModel.logout() },
                isAccepting = uiState.isAcceptingPrivacyConsent,
                error = uiState.privacyConsentError,
            )
        }

        AppScreen.Loading -> {
            // Loading UI mit Escape-Hatch (Logout / Retry), falls der Flow hängt.
            val actuallySignedIn = uiState.isLoggedIn || Firebase.auth.currentUser != null

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(contentPadding)
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                if (uiState.isCheckingApproval || uiState.error == null) {
                    Text("Lade…")
                }

                uiState.error?.let {
                    Text(
                        text = it,
                        color = Color.Red,
                    )
                }

                OutlinedButton(onClick = { mainViewModel.recheckApproval() }) {
                    Text("Neu versuchen")
                }

                if (actuallySignedIn) {
                    OutlinedButton(onClick = { mainViewModel.logout() }) {
                        Text("Logout")
                    }
                }

                Spacer(Modifier.height(8.dp))
                Text(
                    text = "Tipp: Wenn dieser Screen hängen bleibt, kannst du dich hier ausloggen und erneut anmelden.",
                )
            }
        }

        AppScreen.Login -> {
            LoginScreen(
                mainViewModel = mainViewModel,
                onLoginClick = { email, password -> mainViewModel.login(email, password) },
                onForgotPasswordClick = {
                    mainViewModel.clearPasswordResetFeedback()
                    screen = AppScreen.ForgotPassword
                },
                onRegisterClick = { screen = AppScreen.Register },
                onCreateDebugUserClick = { mainViewModel.createDebugUserFromLogin() },
                onLoginWithTestUserClick = { mainViewModel.loginWithTestUserManuallyChecked() },
                onLoginWithTestUserHasMemberNumberClick = { mainViewModel.loginWithTestUserHasMemberNumberNoSport() },
                // Ohne den Abstand lag der Kopf mit dem Logo unter der blauen Kopfzeile.
                contentPadding = contentPadding,
            )
        }

        AppScreen.ForgotPassword -> {
            ForgotPasswordScreen(
                mainViewModel = mainViewModel,
                onBackToLogin = { screen = AppScreen.Login },
                contentPadding = contentPadding,
            )
        }

        AppScreen.Register -> {
            RegisterScreen(
                state = uiState,
                onAddressQueryChanged = mainViewModel::onAddressQueryChanged,
                onAddressSuggestionSelected = mainViewModel::onAddressSuggestionSelected,
                onRegister = { email, password, vorname, nachname, telefonnummer, privacyAccepted ->
                    val placeId = uiState.selectedPlaceId
                    if (placeId != null) {
                        mainViewModel.register(
                            email = email,
                            password = password,
                            confirmPassword = password,
                            firstName = vorname,
                            lastName = nachname,
                            phone = telefonnummer,
                            placeId = placeId,
                            privacyAccepted = privacyAccepted,
                        )
                    }
                },
                onCreateDebugUserClick = { mainViewModel.createDebugUserFromLogin() },
                onBackToLogin = {
                    screen = AppScreen.Login
                },
                isLoading = uiState.isLoading,
                errorMessage = uiState.error,
                contentPadding = contentPadding
            )
        }
    }
}

private fun AppTab.toScreen(): AppScreen = when (this) {
    AppTab.BOOKING -> AppScreen.Booking
    AppTab.MY_GAMES -> AppScreen.MyGames
    AppTab.PROFILE -> AppScreen.Profile
    AppTab.ADMIN -> AppScreen.AdminPanel
}

/** Bereich mit Navigation unten (Redesign Stufe 2): Inhalt oben, Leiste unten. */
@Composable
private fun TabShell(
    selected: AppTab,
    isAdmin: Boolean,
    onSelectTab: (AppTab) -> Unit,
    content: @Composable () -> Unit,
) {
    Column(Modifier.fillMaxSize()) {
        Box(Modifier.weight(1f).fillMaxWidth()) { content() }
        AscBottomBar(selected = selected, isAdmin = isAdmin, onSelect = onSelectTab)
    }
}
